<script setup lang="ts">
  import { z } from 'zod';
  import { EducationLevel, Institution, UserEducation, UserTraining } from '@borg/types';
  import { MpAutocomplete, MpDatePicker, MpInput, MpSelect, pushToastMessage } from '@borg/ui';
  import { EditModalEvents } from './types';

  const props = defineProps<{
    isOpen: boolean;
    isTraining: boolean;
    id?: string;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();

  const { data: educationLevels } = await educationLevelsRepository.getMany();

  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: z.object({
      educationLevel: z
        .object({
          id: z.number().optional(),
          name: z.string().optional(),
        })
        .optional()
        .nullable()
        .refine(
          (educationLevel) => {
            if (props.isTraining) {
              return true;
            }
            return !!educationLevel?.id;
          },
          { message: t('REQUIRED_INPUT') },
        ),
      institution: z.object({
        id: z.number().optional(),
        name: z.string().min(1, t('REQUIRED_INPUT')),
      }),
      orientation: z.string(),
      startedOn: z.date({ invalid_type_error: t('REQUIRED_INPUT') }),
      endedOn: z.union([z.date(), z.null()]),
    }),
  });

  const submitInProgress = ref(false);
  const getInProgress = ref(false);
  const isAwaitingResponse = computed(() => submitInProgress.value || getInProgress.value);

  const TRANSLATION_KEYS = props.isTraining
    ? {
        TITLE: t('MY_PROFILE_PAGE.TRAINING_MODAL.TITLE'),
        INSTITUTION_LABEL: t('MY_PROFILE_PAGE.TRAINING_MODAL.INSTITUTION_LABEL'),
        ORIENTATION_LABEL: t('MY_PROFILE_PAGE.TRAINING_MODAL.ORIENTATION_LABEL'),
        START_DATE_LABEL: t('MY_PROFILE_PAGE.TRAINING_MODAL.START_DATE_LABEL'),
        END_DATE_LABEL: t('MY_PROFILE_PAGE.TRAINING_MODAL.END_DATE_LABEL'),
        END_DATE_DESCRIPTION: t('MY_PROFILE_PAGE.TRAINING_MODAL.END_DATE_DESCRIPTION'),
      }
    : {
        TITLE: t('MY_PROFILE_PAGE.EDUCATION_MODAL.TITLE'),
        EDUCATION_LEVEL_LABEL: t('MY_PROFILE_PAGE.EDUCATION_MODAL.EDUCATION_LEVEL_LABEL'),
        INSTITUTION_LABEL: t('MY_PROFILE_PAGE.EDUCATION_MODAL.INSTITUTION_LABEL'),
        ORIENTATION_LABEL: t('MY_PROFILE_PAGE.EDUCATION_MODAL.ORIENTATION_LABEL'),
        START_DATE_LABEL: t('MY_PROFILE_PAGE.EDUCATION_MODAL.START_DATE_LABEL'),
        END_DATE_LABEL: t('MY_PROFILE_PAGE.EDUCATION_MODAL.END_DATE_LABEL'),
        END_DATE_DESCRIPTION: t('MY_PROFILE_PAGE.EDUCATION_MODAL.END_DATE_DESCRIPTION'),
      };

  async function search(query: string) {
    const result = await institutionsService.search(query);
    return result.isSuccess ? result.data : [];
  }

  function onSelectInstitution(selected: Institution) {
    values.institution = selected;
  }

  function onUpdateInstitutionName(name: Institution['name']) {
    values.institution = { name };
  }

  function transformEntity(input?: UserEducation | UserTraining) {
    return {
      educationLevel: input && 'educationLevel' in input ? input?.educationLevel : undefined,
      institution: input?.institution ?? { name: '' },
      orientation: input?.orientation ?? '',
      startedOn: toDate(input?.startedOn),
      endedOn: toDate(input?.endedOn),
    };
  }

  async function onModalOpened() {
    if (props.id) {
      getInProgress.value = true;
      const { data: education, status } = await educationsRepository.get(props.id);
      if (status.value === 'success' && education.value) {
        setValues(transformEntity(education.value));
        getInProgress.value = false;
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.FETCH_ERROR_TOAST.SUBTITLE'),
        });
        onClose();
      }
    }
  }

  async function onSubmit() {
    const valid = await validate();
    if (valid) {
      const _values = {
        ...values,
        startedOn: getLocaleDate(values.startedOn),
        endedOn: getLocaleDate(values.endedOn),
      };
      try {
        submitInProgress.value = true;
        props.id
          ? await educationsRepository.save({
              ..._values,
              id: props.id,
              isTraining: props.isTraining,
            })
          : await educationsRepository.create({ ..._values, isTraining: props.isTraining });
        submitInProgress.value = false;
        emits('change');
        onClose();
      } catch (e) {
        submitInProgress.value = false;
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="TRANSLATION_KEYS.TITLE"
    :in-progress="isAwaitingResponse"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
  >
    <MyProfileEditModalForm>
      <MpSelect
        v-if="!props.isTraining"
        v-model="values.educationLevel"
        :options="educationLevels ?? []"
        :get-value="(i: EducationLevel) => i.id"
        :get-label="(i: EducationLevel) => i.name"
        :label="$t('MY_PROFILE_PAGE.EDUCATION_MODAL.EDUCATION_LEVEL_LABEL')"
        :error="state.educationLevel.message"
        required
      />
      <MpAutocomplete
        :model-value="values.institution.name"
        :search="search"
        :get-value="(o: Institution) => o.id"
        :get-label="(o: Institution) => o.name"
        :label="TRANSLATION_KEYS.INSTITUTION_LABEL"
        :error="state.institution.message"
        required
        style="width: 100%"
        @update:model-value="onUpdateInstitutionName"
        @selected="onSelectInstitution"
      />
      <MpInput
        v-model="values.orientation"
        :label="TRANSLATION_KEYS.ORIENTATION_LABEL"
        :error="state.orientation.message"
      />
      <span class="form__row-half">
        <MpDatePicker
          v-model="values.startedOn"
          :label="TRANSLATION_KEYS.START_DATE_LABEL"
          :error="state.startedOn.message"
          :max-date="values.endedOn ? values.endedOn : new Date()"
          pick="year"
          clearable
          required
        />
      </span>
      <span class="form__row-half">
        <MpDatePicker
          v-model="values.endedOn"
          :label="TRANSLATION_KEYS.END_DATE_LABEL"
          :error="state.endedOn.message"
          :message="TRANSLATION_KEYS.END_DATE_DESCRIPTION"
          :min-date="values.startedOn ? values.startedOn : undefined"
          :max-date="new Date()"
          pick="year"
          clearable
        />
      </span>
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>
